import {
  type BulkCreateProductGroupEntryPayload,
  type ProductGroupMember,
} from '../../../../infrastructure/ProductGroupClient';

export const MAX_CSV_DATA_ROWS = 100;

export interface ParseProductGroupsCsvResult {
  groups: BulkCreateProductGroupEntryPayload[];
  errors: string[];
}

interface ParsedRow {
  csvRowNumber: number;
  groupName: string;
  attributeValues: string[];
  sku: string;
}

interface FlatRow {
  attributeValuesAtDepth: string[];
  sku: string;
}

export function parseProductGroupsCsv(csvText: string): ParseProductGroupsCsvResult {
  const errors: string[] = [];
  const lines = csvText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (lines.length === 0) {
    return { groups: [], errors: ['CSV is empty'] };
  }

  const dataRowCount = lines.length - 1;
  if (dataRowCount > MAX_CSV_DATA_ROWS) {
    return {
      groups: [],
      errors: [
        `CSV exceeds the ${MAX_CSV_DATA_ROWS}-row limit (got ${dataRowCount} rows). Please split into smaller files.`,
      ],
    };
  }

  const headerCells = parseCsvLine(lines[0]!).map((cell) => cell.trim());
  if (headerCells.length < 3) {
    return { groups: [], errors: ['Header must have at least 3 columns: groupName, <attribute>, sku'] };
  }
  const firstHeader = headerCells[0]!;
  const lastHeader = headerCells[headerCells.length - 1]!;
  if (firstHeader.toLowerCase() !== 'groupname') {
    return { groups: [], errors: [`First column must be "groupName" (got "${firstHeader}")`] };
  }
  if (lastHeader.toLowerCase() !== 'sku') {
    return { groups: [], errors: [`Last column must be "sku" (got "${lastHeader}")`] };
  }
  const attributeColumnNames = headerCells.slice(1, -1);
  if (attributeColumnNames.some((name) => name === '')) {
    return { groups: [], errors: ['Attribute column headers cannot be empty'] };
  }

  const rowsByGroupName = new Map<string, ParsedRow[]>();
  for (let lineIndex = 1; lineIndex < lines.length; lineIndex += 1) {
    const cells = parseCsvLine(lines[lineIndex]!);
    if (cells.length !== headerCells.length) {
      errors.push(`Row ${lineIndex + 1}: expected ${headerCells.length} columns, got ${cells.length}`);
      continue;
    }
    const groupName = cells[0]!.trim();
    const sku = cells[cells.length - 1]!.trim();
    if (!groupName) {
      errors.push(`Row ${lineIndex + 1}: groupName is required`);
      continue;
    }
    if (!sku) {
      errors.push(`Row ${lineIndex + 1}: sku is required`);
      continue;
    }
    const attributeValues = cells.slice(1, -1).map((cell) => cell.trim());
    if (!rowsByGroupName.has(groupName)) {
      rowsByGroupName.set(groupName, []);
    }
    rowsByGroupName.get(groupName)!.push({ csvRowNumber: lineIndex + 1, groupName, attributeValues, sku });
  }

  const groups: BulkCreateProductGroupEntryPayload[] = [];
  rowsByGroupName.forEach((rowsForGroup, groupName) => {
    const filledDepth = computeFilledAttributeDepth(rowsForGroup);
    if (filledDepth === 0) {
      errors.push(`Group "${groupName}": at least one attribute column must have a value`);
      return;
    }
    const usedAttributeNames = attributeColumnNames.slice(0, filledDepth);
    const flatRows = buildFlatRowsAtDepth(rowsForGroup, filledDepth, usedAttributeNames, errors);
    if (flatRows.length === 0) {
      errors.push(`Group "${groupName}": no valid rows`);
      return;
    }
    groups.push({
      groupName,
      memberAttribute: usedAttributeNames[0]!,
      members: nestMembersByAttributePath(flatRows, usedAttributeNames),
    });
  });

  return { groups, errors };
}

function computeFilledAttributeDepth(rows: Array<{ attributeValues: string[] }>): number {
  let depth = 0;
  for (let columnIndex = 0; columnIndex < (rows[0]?.attributeValues.length ?? 0); columnIndex += 1) {
    const anyFilled = rows.some((row) => (row.attributeValues[columnIndex] ?? '').trim() !== '');
    if (anyFilled) {
      depth = columnIndex + 1;
    }
  }
  return depth;
}

function buildFlatRowsAtDepth(
  rows: ParsedRow[],
  filledDepth: number,
  usedAttributeNames: string[],
  errors: string[],
): FlatRow[] {
  const flatRows: FlatRow[] = [];
  rows.forEach((row) => {
    const valuesAtDepth = row.attributeValues.slice(0, filledDepth);
    const missingColumnIndex = valuesAtDepth.findIndex((value) => value === '');
    if (missingColumnIndex >= 0) {
      const missingAttributeName = usedAttributeNames[missingColumnIndex] ?? `attribute ${missingColumnIndex + 1}`;
      errors.push(`Row ${row.csvRowNumber}: missing value for "${missingAttributeName}"; row skipped`);
      return;
    }
    flatRows.push({ attributeValuesAtDepth: valuesAtDepth, sku: row.sku });
  });
  return flatRows;
}

function nestMembersByAttributePath(flatRows: FlatRow[], attributePath: string[]): ProductGroupMember[] {
  if (attributePath.length === 1) {
    return flatRows.map((row) => ({ value: row.attributeValuesAtDepth[0]!, sku: row.sku }));
  }
  const groupedByTopValue = new Map<string, FlatRow[]>();
  const insertionOrder: string[] = [];
  flatRows.forEach((row) => {
    const topValue = row.attributeValuesAtDepth[0]!;
    if (!groupedByTopValue.has(topValue)) {
      groupedByTopValue.set(topValue, []);
      insertionOrder.push(topValue);
    }
    groupedByTopValue.get(topValue)!.push({
      attributeValuesAtDepth: row.attributeValuesAtDepth.slice(1),
      sku: row.sku,
    });
  });
  const [, ...deeperPath] = attributePath;
  return insertionOrder.map((topValue) => ({
    value: topValue,
    memberAttribute: deeperPath[0]!,
    members: nestMembersByAttributePath(groupedByTopValue.get(topValue)!, deeperPath),
  }));
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let charIndex = 0; charIndex < line.length; charIndex += 1) {
    const char = line[charIndex];
    if (char === '"') {
      if (inQuotes && line[charIndex + 1] === '"') {
        current += '"';
        charIndex += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.replace(/^"|"$/g, ''));
}
